import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { LOGIN, REFRESH } from 'src/Api/endpoints';

// Orderbook Aggregator - normalized orderbooks
export const orderBookAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_ORDERBOOK_AGGREGATOR_URL
});

// Profits WebSocket - HTTP endpoints
export const profitsWsAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_PROFITS_WS_URL
});

// Crypto State Manager - read-cryptos & modified-cryptos
export const cryptoStateAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_CRYPTO_STATE_URL
});

// Arbitrage Calculator (Rust) - calculation stats
export const calculatorAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_CALCULATOR_URL
});

// Prediction Manager - pair CRUD, matching, and system on/off
export const predictionManagerAxiosApi = axios.create({
  baseURL:
    import.meta.env.VITE_PREDICTION_MANAGER_URL ||
    import.meta.env.VITE_PREDICTION_PAIR_MANAGER_URL
});

// Prediction Calculator - arbitrage calculations for prediction markets
export const predictionCalculatorAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_PREDICTION_CALCULATOR_URL
});

// Prediction WebSocket - real-time prediction opportunities
export const predictionWsAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_PREDICTIONS_WS_URL
});

// Live-events prediction WebSocket (Phase 3 — parallel low-latency pipeline).
// Same image as predictionWsAxiosApi, different deployment + port. Falls
// back to the regular URL when the env var isn't set so dev environments
// without the live deployment still render the Live tab against the shared
// WS (just without the sub-100ms latency).
export const livePredictionWsAxiosApi = axios.create({
  baseURL:
    import.meta.env.VITE_LIVE_PREDICTIONS_WS_URL ||
    import.meta.env.VITE_PREDICTIONS_WS_URL
});

// Prediction Arbitrage Manager - positions and trade execution
export const predictionArbitrageManagerAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_PREDICTION_ARBITRAGE_MANAGER_URL
});

// Position Unwinder - unwinding rules/executions API.
// `timeout` is load-bearing here: these instances carry the rule on/off
// toggles, and a dropped/hung toggle must REJECT so the UI surfaces the
// failure and rolls back instead of hanging on the optimistic OFF state.
export const positionUnwinderAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_POSITION_UNWINDER_URL + '/api/position-unwinder',
  timeout: 30000
});

// Position Opener / Entry - entry rules/executions API (see timeout note above)
export const positionOpenerAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_POSITION_OPENER_URL + '/api/position-opener',
  timeout: 30000
});

// Notifications WebSocket service - paginated history feed
export const notificationsAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_NOTIFICATIONS_WS_URL
});

const ALL_APIS = [
  orderBookAxiosApi,
  profitsWsAxiosApi,
  cryptoStateAxiosApi,
  calculatorAxiosApi,
  predictionManagerAxiosApi,
  predictionCalculatorAxiosApi,
  predictionWsAxiosApi,
  livePredictionWsAxiosApi,
  predictionArbitrageManagerAxiosApi,
  positionUnwinderAxiosApi,
  positionOpenerAxiosApi,
  notificationsAxiosApi
];

// Request interceptor: attach Bearer token from localStorage
ALL_APIS.forEach(api =>
  api.interceptors.request.use(
    async config => {
      const token = _getToken();
      if (token) {
        config.headers = {
          ...config.headers,
          authorization: `Bearer ${token}`,
          Accept: 'application/json'
        };
      }
      return config;
    },
    error => Promise.reject(error)
  )
);

// Response interceptor: on 401, try a silent /auth/refresh once, then
// replay the original request with the new token. If refresh itself
// fails (e.g. session max-age reached), let the 401 propagate so the
// AuthProvider falls back to the login screen.
type RetryableConfig = AxiosRequestConfig & { _authRetry?: boolean };

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return url.endsWith(LOGIN) || url.endsWith(REFRESH);
}

ALL_APIS.forEach(api =>
  api.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined;
      const status = error.response?.status;

      if (
        status !== 401 ||
        !config ||
        config._authRetry ||
        isAuthEndpoint(config.url)
      ) {
        return Promise.reject(error);
      }

      // Lazy-load to break a potential circular import at module init.
      const { refreshAuthToken } = await import(
        'src/Common/context/AuthContext'
      );
      const newToken = await refreshAuthToken();
      if (!newToken) return Promise.reject(error);

      config._authRetry = true;
      config.headers = {
        ...config.headers,
        authorization: `Bearer ${newToken}`
      };
      return api.request(config);
    }
  )
);

export function _getToken(): string {
  return localStorage.getItem(import.meta.env.VITE_AUTH_TOKEN_KEY!) || '';
}
